
/* eslint-disable @typescript-eslint/camelcase */
import {
  directDownloadFile,
  fileExtensionFromUrl,
  fileNameFromUrl,
  formatDateNormal,
  sleep
} from "@/app/infrastructures/misc/Utils";
import downloadFile from "@/app/infrastructures/misc/common-library/DownloadFile";
import parsingLinkGcpToAws from "@/app/infrastructures/misc/common-library/ParsingLinkGcpToAws";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ClientController } from "@/app/ui/controllers/ClientController";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { ReportListDownloadData } from "@/domain/entities/Report";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import groupBy from "lodash/groupBy";
import { Options, Vue, prop } from "vue-class-component";

class Props {
  customTitle = prop<string>({
    default: "",
    type: String
  });
  clientId = prop<number>({
    required: true,
    type: Number
  });
  type = prop<string>({
    default: "client_rate",
    type: String
  });
  isCloseable = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  components: {
    Skeleton
  },
  emits: ["close"]
})
export default class WidgetBulkDownloadClient extends Vue.with(Props) {
  isOpen = false;
  requestInInterval: any = null;

  page = 1;
  limit = 20;
  intersectionObserver: IntersectionObserver | null = null;

  mounted() {
    ClientController.clientRateBulkDownload.pagination.limit = 20;
    this.initiateInfiniteScroll();
    this.initiateGetListBulkDownload();
  }

  unmounted() {
    clearInterval(this.requestInInterval);
    this.intersectionObserver?.disconnect();
  }

  get headerTitle() {
    return this.customTitle || this.$t("clientManagement.infoPriceClient.bulkDownloadList");
  }

  get isLoading() {
    return ClientController.isLoadingClientRateBulkDownload;
  }

  get list() {
    return ClientController.clientRateBulkDownload.data;
  }

  get haveWaiting() {
    return this.list.some((data) => data.status === "waiting");
  }

  get downloadRequestList() {
    const data = this.list.map((item) => {
      const date = formatDateNormal(item.createdAt, "DD MMM YYYY");
      return {
        ...item,
        title: this.reportTitle(item),
        dateString: date
      };
    });
    const grouped = groupBy(data, "dateString");
    return Object.keys(grouped).map((item) => grouped[item]);
  }

  get isShowRunningText() {
    return !!FlagsMedusa.information_running_text.getValue().replace(" | ", "");
  }

  initiateGetListBulkDownload() {
    this.getListBulkDownload();
    this.requestInInterval = setInterval(() => {
      if (this.haveWaiting) {
        this.getListBulkDownload();
      }
    }, 10000);
  }

  initiateInfiniteScroll() {
    const getNewData = () => {
      if (this.isLoading) return;
      ClientController.clientRateBulkDownload.pagination.limit += 10;
      this.getListBulkDownload();
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          getNewData();
        }
      });
    }, {});

    const element: any = document.querySelector("#bulk-download-infinite-scroll");
    if (element) this.intersectionObserver.observe(element);
  }

  async getListBulkDownload() {
    const limit = ClientController.clientRateBulkDownload.pagination.limit;
    await ClientController.getClientRateBulkDownloadList({
      page: this.page,
      limit,
      type: this.type,
      clientId: this.clientId,
      isTotalData: false
    });
  }

  onToggleMenu() {
    this.isOpen = !this.isOpen;
  }

  normalizeExtension(ext: string) {
    if (!ext) return ".xlsx";
    return ext.startsWith(".") ? ext : `.${ext}`;
  }

  downloadFileName(item: ReportListDownloadData, fileUrl: string) {
    if (item.filename) return item.filename;
    const fromUrl = decodeURIComponent(fileNameFromUrl(fileUrl));
    if (fromUrl) return fromUrl;
    return `report_${new Date(item.createdAt).getTime()}`;
  }

  async onDownload(item: ReportListDownloadData) {
    MainAppController.showLoading();
    const files = item.url.split(",");
    try {
      for (const file of files) {
        if (file) {
          if (item.isRequireToken) {
            downloadFile(
              file,
              this.downloadFileName(item, file),
              this.normalizeExtension(fileExtensionFromUrl(file)),
              true,
              60000,
              "GET"
            );
          } else {
            directDownloadFile(parsingLinkGcpToAws(file));
          }
          await sleep(500);
        }
      }
    } finally {
      MainAppController.closeLoading();
    }
  }

  reportTitle(item: ReportListDownloadData) {
    if (item.filename) return item.filename;
    const firstUrl = (item.url ?? '').split(",").find((u) => !!u) || "";
    const fromUrl = decodeURIComponent(fileNameFromUrl(firstUrl));
    if (fromUrl) return fromUrl;
    const date = formatDateNormal(item.createdAt, "YYYYMMDDHHmmss");
    return `Report ${date}`;
  }

  onClose(e: any) {
    e.stopPropagation();
    this.isOpen = false;
    this.$emit("close");
  }
}
